import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDropdownStyles } from "components/FDFilterDropdown/FDFilterDropdownStyles";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiPopoverTitle,
  useGeneratedHtmlId,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiText,
  EuiAccordion,
  EuiNotificationBadge,
  EuiIcon,
} from "@elastic/eui";
import { isNullOrUndefined } from "utils";
import { badgeColors } from "Consts";
import Button from "components/Buttons/Button";
import { CustomCheckbox } from "components/CustomInputs/CustomCheckbox";
import IconButton from "components/Buttons/IconButton";
import FDFilterDropdownFooter from "components/FDFilterDropdown/components/FDFilterDropdownFooter";
import Popover from "components/Popover/Popover";
import Badge from "components/Badge/Badge";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

const AccordionItem = ({
  option,
  activeFilters,
  filterItemClick,
  classes,
  setActiveFilters,
  onToggle,
  isOpen,
  filterText,
  singleTopLevelSelection,
  singleSelection,
  onEdit,
  canCreateUpdateGlobalPerfMonTemplates = true,
  canCreateUpdatePrivatePerfMonTemplates = true,
  canCreateUpdateGlobalViews = true,
  canCreateUpdatePrivateViews = true,
}: any) => {
  const processedTagOptions = useMemo(() => {
    return option?.values?.length > 0
      ? option?.values?.map((i: any) => {
          const fullTagName = `${i.id}: ${i?.value}`;
          return {
            ...i,
            name: i?.value,
            fullTagName,
            checked: activeFilters[fullTagName] === "on" ? "on" : undefined,
            key: option?.name,
            viewType: option?.type,
          };
        })
      : [
          {
            ...option,
            name: option?.name,
            fullTagName: option?.name,
            checked: activeFilters[option?.name] === "on" ? "on" : undefined,
            key: option?.name,
          },
        ];
  }, [option, activeFilters]);

  const filteredTagOptions = useMemo(() => {
    let options = processedTagOptions;

    if (filterText) {
      options = processedTagOptions.filter((tagOption: any) => {
        const tagValue = tagOption.name || "";
        const tagDisplay = tagOption.display || tagValue;

        return (
          tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
          tagDisplay.toLowerCase().includes(filterText.toLowerCase())
        );
      });
    }

    return options.sort((a: any, b: any) => {
      const aDisplay = a.display || a.name || "";
      const bDisplay = b.display || b.name || "";
      return aDisplay.localeCompare(bDisplay);
    });
  }, [processedTagOptions, filterText]);

  const checkedCount = useMemo(() => {
    return processedTagOptions.filter(
      (tagOption: any) => activeFilters[tagOption.fullTagName] === "on"
    ).length;
  }, [processedTagOptions, activeFilters]);

  const handleTagOptionClick = (option: any) => {
    const parsedSelected = {
      name: option.fullTagName,
      value: option.fullTagName,
      label: option.fullTagName,
      display: option.fullTagName,
      key: option.key,
    };
    filterItemClick(parsedSelected);
  };

  const getIconType = (option: any) => {
    if (option.type === "local") return "user";
    if (option.type === "global") return "globe";
    if (option.type === "system") return "layers";
    return "layers";
  };

  const getBadge = (i: any) => {
    if (i?.isUserDefault) {
      return <Badge text={"Default"} color={"#4cd09f"} style={{ color: "#343741" }} />;
    }
    if (i?.isSystem) {
      return <Badge text={"System"} color={"#4cd09f"} style={{ color: "#343741" }} />;
    }
    if (!i?.isSystem && i?.isMetricsOnly) {
      return <Badge text={"Metrics Only"} color={"#ffb547"} style={{ color: "#343741" }} />;
    }
    return "";
  };

  const canEditItem = (tagOption: any) => {
    if (!tagOption?.modificationRules?.canUpdate) {
      return false;
    }

    const viewType = tagOption?.viewType;

    switch (viewType) {
      case "global":
        return canCreateUpdateGlobalPerfMonTemplates && canCreateUpdateGlobalViews;
      case "local":
        return canCreateUpdatePrivatePerfMonTemplates && canCreateUpdatePrivateViews;
      case "system":
        return true;
      default:
        return false;
    }
  };

  const renderTagOption = (tagOption: any) => {
    return (
      <div
        key={tagOption.fullTagName}
        className={classes.tagOptionRow}
        onClick={() => handleTagOptionClick(tagOption)}
        style={{ cursor: "pointer", padding: "4px 12px", height: "34px" }}>
        <EuiFlexGroup
          style={{
            paddingLeft: "8px",
          }}
          justifyContent="spaceBetween"
          alignItems="center"
          gutterSize="s">
          <CustomCheckbox
            onClick={() => {}}
            isChecked={activeFilters[tagOption.fullTagName] === "on"}
            isExcluded={activeFilters[tagOption.fullTagName] === "off"}
          />
          <EuiFlexItem>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: tagOption?.name?.length > 50 ? "11px" : "14px",
              }}>
              {tagOption?.type && <b>{tagOption?.type}:&nbsp;</b>}
              {tagOption.display ?? tagOption.name}
            </span>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <span>{getBadge(tagOption?.attributes)}</span>
          </EuiFlexItem>
          {canEditItem(tagOption) && (
            <EuiFlexItem grow={false}>
              <IconButton
                size="xs"
                type="pencil"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(tagOption);
                }}
              />
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </div>
    );
  };

  return (
    <div className={classes.tagAccordionButton}>
      <EuiAccordion
        id={`accordion-${option.name}`}
        buttonContent={
          <EuiFlexGroup
            style={{
              height: "33px",
            }}
            alignItems="center"
            gutterSize="s">
            <EuiIcon type={getIconType(option)} color="#07C" />

            <EuiFlexItem>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {option.display ?? option.name}
              </span>
            </EuiFlexItem>
            {checkedCount > 0 && (
              <EuiFlexItem grow={false}>
                <EuiNotificationBadge>{checkedCount}</EuiNotificationBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
        paddingSize="s"
        buttonProps={{
          style: {
            width: "100%",
            display: "block",
          },
        }}
        forceState={isOpen ? "open" : "closed"}
        onToggle={(isOpen) => {
          if (onToggle) {
            option?.values?.length > 0 ? onToggle(option.name, isOpen) : "";
          }
        }}
        extraAction={null}
        arrowDisplay={option?.values?.length > 0 ? "right" : "none"}>
        <div style={{ marginTop: "0" }}>{filteredTagOptions.map(renderTagOption)}</div>
      </EuiAccordion>
    </div>
  );
};

const ViewTemplateSelector = ({
  Items = [],
  Name = "",
  Field = "",
  AllowExclude = false,
  popoverButtonProps = {},
  filterObject,
  setFilterObject,
  singleTopLevelSelection = true,
  isLoading = false,
  disableSorting = false,
  anchorPosition = "downLeft",
  singleSelection = false,
  onCreate = () => {},
  onEdit = () => {},
  onCancel = () => {},
  onApply = () => {},
  perfmonFilterId = null,
  actionButtonEnabled = true,
  canCreateUpdateGlobalPerfMonTemplates = true,
  canCreateUpdatePrivatePerfMonTemplates = true,
  canCreateUpdateGlobalViews = true,
  canCreateUpdatePrivateViews = true,
}: any) => {
  const classes = useDropdownStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [items, setItems] = useState<any>([]);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [filterText, setFilterText] = useState<string>("");
  const [stableOrder, setStableOrder] = useState([]);
  const [openAccordions, setOpenAccordions] = useState<any>({});
  const { clientId, GlobalTopFilterObject } = useSelector(globalFilterSelector);
  const noFilters = Object.values(GlobalTopFilterObject)?.every((i: any) => i?.length === 0);
  const isTemplate = Field === "template";
  const isDashboard = Field === "dashboard";

  const publishGlobalFilter = useCallback(
    async (values: any) => {
      const newFieldGlobalFilterValues = Object.entries(values)
        .filter(([_, value]) => value !== undefined)
        .map(([listItem, _]) => listItem);

      setActiveFilters({});
      setFilterObject(newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : []);
    },
    [Field, clientId, AllowExclude]
  );

  useEffect(() => {
    if (Items) {
      const cleanItems = Items.filter((option: any) => !isNullOrUndefined(option.value));

      const mappedItems = cleanItems.map((f: any) => ({
        ...f,
        name: f.value,
      }));

      setItems(mappedItems);
    }
  }, [Items]);

  useEffect(() => {
    const map: any = {};
    if (filterObject) {
      for (const filterVal of filterObject) {
        map[filterVal] = "on";
      }

      setActiveFilters(map);
    } else {
      resetSelected();
    }
  }, [filterObject, JSON.stringify(items)]);

  const togglePopover = () => {
    const newIsOpen = !isPopoverOpen;
    if (newIsOpen) {
      recalculateOrder();
    }
    setIsPopoverOpen(newIsOpen);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setOpenAccordions({});
  };

  const resetSelected = useCallback(() => {
    if (isNullOrUndefined(filterObject) || Object.keys(filterObject).length === 0) {
      setActiveFilters({});
    }
  }, [Name, JSON.stringify(activeFilters), JSON.stringify(items), filterObject]);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const activeFiltersCount = Object.keys(activeFilters).length;

  const filterArrowDownButton = (
    <EuiFilterButton
      isLoading={isLoading}
      className={
        activeFiltersCount == 0
          ? "euiFilterButton__notification_hidden filter_button"
          : "filter_button"
      }
      data-test-subj={`filter-dd-${Name}`}
      hasActiveFilters={activeFiltersCount > 0}
      iconType="arrowDown"
      iconSize="s"
      isSelected={isPopoverOpen}
      numActiveFilters={activeFiltersCount}
      numFilters={items.length}
      onClick={togglePopover}
      {...popoverButtonProps}>
      <span style={{ position: "absolute", top: "5px" }}>
        {/* <EuiIcon type={isTemplate ? "layers" : "controlsHorizontal"} /> */}
        <EuiIcon type={"layers"} />
      </span>

      <span style={{ paddingLeft: "20px" }}>{Name}</span>
    </EuiFilterButton>
  );

  const filterItemClick = (item: any) => {
    const currentChecked = item ? activeFilters[item.name] : undefined;
    let newChecked;

    newChecked = currentChecked === "on" ? undefined : "on";

    const filterListStatusCopy: any = { ...(!singleSelection && activeFilters) };

    if (singleSelection) {
      for (const key in filterListStatusCopy) {
        if (!key.startsWith(item.key)) {
          delete filterListStatusCopy[key];
        }
      }
    }

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }
    setActiveFilters(filterListStatusCopy);
  };

  const applyFilters = () => {
    setFilterText("");
    publishGlobalFilter(activeFilters);
    if (onApply && (isTemplate || isDashboard)) {
      const keys = Object.keys(activeFilters);
      if (keys?.length > 0) {
        const ids = keys.map((key) => key.split(":")[0]);
        if (ids?.length > 0) onApply(ids);
      } else {
        setActiveFilters({});
        setFilterObject([]);
      }
    }
    closePopover();
    recalculateOrder();
  };

  const cancelFiltersSelection = () => {
    setFilterText("");
    onCancel();
    closePopover();
  };

  const serachId = useGeneratedHtmlId();

  const dropdownOptions: any[] = items
    .filter((item: any) => {
      const accordionHeaderMatches =
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

      if (accordionHeaderMatches) return true;

      if (item.values && item.values.length > 0) {
        return item.values.some((tagOption: any) => {
          const tagValue = tagOption.value || "";
          const tagDisplay = tagOption.display || tagValue;

          return (
            tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
            tagDisplay.toLowerCase().includes(filterText.toLowerCase())
          );
        });
      }

      return false;
    })
    .sort((a: any, b: any) => a.name - b.name)
    .map((i: any) => ({
      ...i,
      label: i.name,
    }));

  const getCheckedCountForOption = (option: any) => {
    return singleTopLevelSelection
      ? option.values?.reduce((count: any, item: any) => {
          const fullTagName = `${option.name}: ${item?.value}`;
          return activeFilters[fullTagName] === "on" || activeFilters[fullTagName] === "off"
            ? count + 1
            : count;
        }, 0) || 0
      : activeFilters[option.name] === "on"
        ? 1
        : 0;
  };

  const recalculateOrder = useCallback(() => {
    const filteredItems = items.filter((item: any) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    });

    const newOrder = [...filteredItems].sort((a, b) => {
      const aCheckedCount = getCheckedCountForOption(a);
      const bCheckedCount = getCheckedCountForOption(b);

      if (aCheckedCount > 0 && bCheckedCount === 0) return -1;
      if (aCheckedCount === 0 && bCheckedCount > 0) return 1;

      if (aCheckedCount > 0 && bCheckedCount > 0) {
        return bCheckedCount - aCheckedCount;
      }

      const aText = a.display || a.name;
      const bText = b.display || b.name;
      return aText.localeCompare(bText);
    });

    //@ts-ignore
    setStableOrder(newOrder.map((item) => item.name));
  }, [items, filterText, activeFilters]);

  useEffect(() => {
    if (items.length > 0) {
      recalculateOrder();
    }
  }, [items]);

  const handleAccordionToggle = (optionName: any, isOpen: any) => {
    if (isOpen) {
      setOpenAccordions({ [optionName]: true });
    } else {
      setOpenAccordions((prev: any) => {
        const newOpenAccordions: any = { ...prev };
        delete newOpenAccordions[optionName];
        return newOpenAccordions;
      });
    }
  };

  const getSortedDropdownOptions = () => {
    if (disableSorting) return dropdownOptions;
    if (stableOrder.length === 0) {
      return dropdownOptions;
    }

    const itemMap: any = {};
    dropdownOptions.forEach((item) => {
      itemMap[item.name] = item;
    });

    return stableOrder.filter((name) => itemMap[name]).map((name) => itemMap[name]);
  };

  useEffect(() => {
    if (filterText.length > 0) {
      const newOpenAccordions: any = {};

      items.forEach((item: any) => {
        const headerMatches =
          (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
          (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

        if (item.values && item.values.length > 0) {
          const hasMatchingChildren = item.values.some((tagOption: any) => {
            const tagValue = tagOption.value || "";
            const tagDisplay = tagOption.display || tagValue;

            return (
              tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
              tagDisplay.toLowerCase().includes(filterText.toLowerCase())
            );
          });

          if (headerMatches || hasMatchingChildren) {
            newOpenAccordions[item.name] = true;
          }
        } else if (headerMatches) {
          newOpenAccordions[item.name] = true;
        }
      });

      setOpenAccordions(newOpenAccordions);
    } else if (filterText.length === 0) {
      setOpenAccordions({});
    }
  }, [filterText, items]);

  return (
    <>
      {
        <Popover
          noCloseOnScroll
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cancelFiltersSelection();
            }
          }}
          anchorPosition={anchorPosition}
          button={filterArrowDownButton}
          onClose={cancelFiltersSelection}
          isOpen={isPopoverOpen}
          ownFocus
          children={
            <div
              style={{
                backgroundColor: "var(--card-background)",
                borderRadius: "var(--box-radius)",
              }}>
              <EuiPopoverTitle className={classes.filterPopoverTitle}>
                <EuiFieldSearch
                  className={`${classes.search_input} filter-search-input`}
                  id={serachId}
                  isClearable
                  onChange={(e) => {
                    setFilterText(e.target.value);
                  }}
                  style={{
                    borderBottom: ".6px solid var(--border-color)",
                    backgroundColor: "var(--card-background)",
                  }}
                  value={filterText}
                />
              </EuiPopoverTitle>
              <div
                className="euiFilterSelect__items"
                style={{
                  overflowY: "auto",
                  maxHeight: "400px",
                  scrollbarWidth: "thin",
                  minWidth: "400px",
                  backgroundColor: "var(--card-background)",
                }}>
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                    <EuiFlexGroup
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                      gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiLoadingSpinner size="s" />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText>
                          <p style={{ fontSize: "var(--fs12)", color: "#516381" }}>Loading data</p>
                        </EuiText>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                ) : getSortedDropdownOptions().length > 0 ? (
                  getSortedDropdownOptions().map((option) => (
                    <AccordionItem
                      key={option.name || option.label}
                      option={option}
                      activeFilters={activeFilters}
                      filterItemClick={filterItemClick}
                      classes={classes}
                      setActiveFilters={setActiveFilters}
                      onToggle={handleAccordionToggle}
                      isOpen={!!openAccordions[option.name]}
                      filterText={filterText}
                      singleTopLevelSelection={singleTopLevelSelection}
                      singleSelection={singleSelection}
                      onEdit={(option: any) => {
                        onEdit(option);
                        closePopover();
                      }}
                      canCreateUpdateGlobalPerfMonTemplates={canCreateUpdateGlobalPerfMonTemplates}
                      canCreateUpdatePrivatePerfMonTemplates={
                        canCreateUpdatePrivatePerfMonTemplates
                      }
                      canCreateUpdateGlobalViews={canCreateUpdateGlobalViews}
                      canCreateUpdatePrivateViews={canCreateUpdatePrivateViews}
                    />
                  ))
                ) : (
                  <div style={{ padding: "16px", textAlign: "center" }}>No data found</div>
                )}
              </div>

              <FDFilterDropdownFooter
                onApply={applyFilters}
                onCancel={cancelFiltersSelection}
                leftChildren={
                  actionButtonEnabled &&
                  (canCreateUpdateGlobalViews || canCreateUpdatePrivateViews) ? (
                    <Button
                      type="empty"
                      text={
                        isDashboard
                          ? "Save as Dashboard"
                          : isTemplate
                            ? "Save as Template"
                            : "Save Current View"
                      }
                      onClick={() => {
                        onCreate();
                        closePopover();
                      }}
                      disabled={isTemplate || isDashboard ? isNullOrUndefined(onCreate) : noFilters}
                    />
                  ) : null
                }
              />
            </div>
          }
        />
      }
    </>
  );
};

export default ViewTemplateSelector;
